import {
  addDays,
  appointmentsForLocationOnDay,
  appointmentsInWeek,
  atMinutes,
  bookAppointment,
  canBookAppointment,
  CONSULTATION_SERVICE_ID,
  findPatient,
  findProvider,
  findService,
  formatClock,
  formatDayHeading,
  formatPrice,
  formatTimeRange,
  formatWeekHeading,
  loadAppointments,
  providersForLocation,
  readCalendarDate,
  readCalendarMode,
  searchPatients,
  SEED_SERVICES,
  SLOT_MINUTES,
  slotStarts,
  startOfWeek,
  updateAppointmentNotes,
  writeCalendarDate,
  writeCalendarMode,
  type Appointment,
  type CalendarMode,
  type SeedPatient,
  type SeedProvider,
} from '../clinic/booking';
import { readStaffAppContext } from '../clinic/seed';
import { el } from '../dom';

interface SlotTarget {
  readonly day: Date;
  readonly minutes: number;
  readonly providerId: string | null;
}

let onCalendarEscape: ((event: KeyboardEvent) => void) | null = null;

export function renderCalendar(mount: HTMLElement): void {
  const context = readStaffAppContext();
  if (!context) {
    mount.append(el('p', { class: 'lede' }, 'Open the staff door to view the calendar.'));
    return;
  }

  const storage = sessionStorage;
  const location = context.location;
  const providers = providersForLocation(location.id);
  let mode = readCalendarMode(storage);
  let cursor = readCalendarDate(storage);
  let selectedPatient: SeedPatient | null = null;

  const toolbar = el('div', { class: 'cal-toolbar' });
  const grid = el('div', { class: 'cal-grid-host', 'data-slot': 'calendar-grid' });
  const drawer = el('aside', { class: 'cal-drawer', hidden: 'true', 'aria-label': 'Appointment' });
  const backdrop = el('div', { class: 'cal-backdrop', hidden: 'true' });

  const root = el(
    'section',
    { class: 'cal' },
    el(
      'header',
      { class: 'cal-head' },
      el('p', { class: 'cal-kicker' }, 'BOOKING / EMR'),
      el('p', { class: 'note cal-note' }, `${location.name} · ${location.code}. Seed patients only. No live Stripe.`),
    ),
    toolbar,
    grid,
    backdrop,
    drawer,
  );
  mount.append(root);

  function closeDrawer(): void {
    drawer.setAttribute('hidden', 'true');
    backdrop.setAttribute('hidden', 'true');
    drawer.replaceChildren();
  }

  function openDrawer(content: HTMLElement): void {
    drawer.replaceChildren(content);
    drawer.removeAttribute('hidden');
    backdrop.removeAttribute('hidden');
  }

  backdrop.addEventListener('click', () => {
    closeDrawer();
  });

  const onEscape = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !drawer.isConnected || drawer.hasAttribute('hidden')) {
      return;
    }
    closeDrawer();
  };
  if (onCalendarEscape) {
    document.removeEventListener('keydown', onCalendarEscape);
  }
  document.addEventListener('keydown', onEscape);
  onCalendarEscape = onEscape;

  function setMode(next: CalendarMode): void {
    mode = next;
    writeCalendarMode(next, storage);
    paint();
  }

  function setCursor(next: Date): void {
    cursor = next;
    writeCalendarDate(next, storage);
    paint();
  }

  function appointments(): Appointment[] {
    return loadAppointments(storage);
  }

  function paintToolbar(): void {
    const today = new Date();
    const label = mode === 'day' ? formatDayHeading(cursor) : formatWeekHeading(cursor);
    const dayBtn = el(
      'button',
      {
        class: mode === 'day' ? 'cal-seg cal-seg--on' : 'cal-seg',
        type: 'button',
        'aria-pressed': mode === 'day' ? 'true' : 'false',
      },
      'Day',
    );
    const weekBtn = el(
      'button',
      {
        class: mode === 'week' ? 'cal-seg cal-seg--on' : 'cal-seg',
        type: 'button',
        'aria-pressed': mode === 'week' ? 'true' : 'false',
      },
      'Week',
    );
    dayBtn.addEventListener('click', () => {
      setMode('day');
    });
    weekBtn.addEventListener('click', () => {
      setMode('week');
    });

    const prev = el('button', { class: 'cal-iconbtn', type: 'button', 'aria-label': 'Previous' }, '‹');
    const next = el('button', { class: 'cal-iconbtn', type: 'button', 'aria-label': 'Next' }, '›');
    const todayBtn = el('button', { class: 'cal-iconbtn', type: 'button' }, 'Today');
    prev.addEventListener('click', () => {
      setCursor(addDays(cursor, mode === 'week' ? -7 : -1));
    });
    next.addEventListener('click', () => {
      setCursor(addDays(cursor, mode === 'week' ? 7 : 1));
    });
    todayBtn.addEventListener('click', () => {
      setCursor(today);
    });

    const add = el('button', { class: 'door__button door__button--live', type: 'button' }, 'New appointment');
    add.addEventListener('click', () => {
      openNewAppointment({ day: cursor, minutes: 10 * 60, providerId: providers[0]?.id ?? null });
    });

    toolbar.replaceChildren(
      el('div', { class: 'cal-toolbar__nav' }, todayBtn, prev, next, el('h2', { class: 'cal-date' }, label)),
      el('div', { class: 'cal-toolbar__meta' }, el('span', { class: 'cal-staff' }, 'All staff'), el('div', { class: 'cal-segset' }, dayBtn, weekBtn), add),
    );
  }

  function eventButton(appointment: Appointment): HTMLButtonElement {
    const patient = findPatient(appointment.patientId);
    const service = findService(appointment.serviceId);
    const provider = findProvider(appointment.providerId);
    const button = el(
      'button',
      {
        class: 'cal-event',
        type: 'button',
        'data-appointment-id': appointment.id,
      },
      el('span', { class: 'cal-event__name' }, patient?.displayName ?? appointment.patientId),
      el('span', { class: 'cal-event__meta' }, service?.name ?? 'Service'),
      el('span', { class: 'cal-event__meta' }, provider?.displayName ?? ''),
    );
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      openDetails(appointment.id);
    });
    return button;
  }

  function paintDayGrid(): void {
    const rows = slotStarts();
    const cols = Math.max(providers.length, 1);
    grid.style.setProperty('--cal-cols', String(cols));
    const table = el('div', { class: 'cal-board cal-board--day' });
    table.append(el('div', { class: 'cal-corner' }, 'Time'));
    for (const provider of providers) {
      table.append(el('div', { class: 'cal-colhead' }, provider.displayName));
    }
    if (providers.length === 0) {
      table.append(el('div', { class: 'cal-colhead' }, 'No providers at this location'));
    }

    const dayAppointments = appointmentsForLocationOnDay(appointments(), location.id, cursor);
    for (const minutes of rows) {
      table.append(el('div', { class: 'cal-time' }, formatClock(minutes)));
      const columnProviders: Array<SeedProvider | null> =
        providers.length > 0 ? [...providers] : [null];
      for (const provider of columnProviders) {
        const slot = el('button', {
          class: 'cal-slot',
          type: 'button',
          'data-minutes': String(minutes),
          'data-provider': provider?.id ?? '',
        });
        slot.addEventListener('click', () => {
          openNewAppointment({ day: cursor, minutes, providerId: provider?.id ?? null });
        });
        const hits = dayAppointments.filter((appointment) => {
          const start = new Date(appointment.startMs);
          const startMin = start.getHours() * 60 + start.getMinutes();
          return (
            startMin >= minutes &&
            startMin < minutes + SLOT_MINUTES &&
            (provider ? appointment.providerId === provider.id : true)
          );
        });
        for (const appointment of hits) {
          slot.append(eventButton(appointment));
        }
        table.append(slot);
      }
    }
    grid.replaceChildren(table);
  }

  function paintWeekGrid(): void {
    const weekStart = startOfWeek(cursor);
    const days = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
    const rows = slotStarts();
    grid.style.setProperty('--cal-cols', '7');
    const table = el('div', { class: 'cal-board cal-board--week' });
    table.append(el('div', { class: 'cal-corner' }, 'Time'));
    for (const day of days) {
      table.append(
        el(
          'div',
          { class: 'cal-colhead' },
          formatDayHeading(day).split(',')[0] ?? '',
          el('span', { class: 'cal-colhead__sub' }, String(day.getDate())),
        ),
      );
    }

    const weekAppointments = appointmentsInWeek(appointments(), location.id, weekStart);
    for (const minutes of rows) {
      table.append(el('div', { class: 'cal-time' }, formatClock(minutes)));
      for (const day of days) {
        const slot = el('button', {
          class: 'cal-slot',
          type: 'button',
          'data-minutes': String(minutes),
          'data-day': String(day.getTime()),
        });
        slot.addEventListener('click', () => {
          openNewAppointment({
            day,
            minutes,
            providerId: providers[0]?.id ?? null,
          });
        });
        const hits = weekAppointments.filter((appointment) => {
          const start = new Date(appointment.startMs);
          const startMin = start.getHours() * 60 + start.getMinutes();
          return (
            startMin >= minutes &&
            startMin < minutes + SLOT_MINUTES &&
            start.toDateString() === day.toDateString()
          );
        });
        for (const appointment of hits) {
          slot.append(eventButton(appointment));
        }
        table.append(slot);
      }
    }
    grid.replaceChildren(table);
  }

  function paint(): void {
    paintToolbar();
    if (mode === 'week') {
      paintWeekGrid();
    } else {
      paintDayGrid();
    }
  }

  function openNewAppointment(target: SlotTarget): void {
    selectedPatient = null;
    const start = atMinutes(target.day, target.minutes);
    const title = el('header', { class: 'cal-drawer__head' }, el('h2', {}, 'New Appointment'), closeButton());

    const patientSearch = el('input', {
      class: 'field__control',
      type: 'search',
      placeholder: 'Search seed patients…',
      'aria-label': 'Patient search',
    }) as HTMLInputElement;
    const patientResults = el('ul', { class: 'cal-search', 'data-slot': 'patient-results' });
    const patientSelected = el('p', { class: 'field__value', 'data-slot': 'patient-selected' }, 'No patient selected');

    function paintPatients(query: string): void {
      patientResults.replaceChildren();
      for (const patient of searchPatients(query)) {
        const item = el(
          'li',
          {},
          el(
            'button',
            { class: 'cal-search__item', type: 'button', 'data-patient-id': patient.id },
            `${patient.displayName} · ${patient.code}`,
          ),
        );
        item.querySelector('button')?.addEventListener('click', () => {
          selectedPatient = patient;
          patientSelected.textContent = `${patient.displayName} · ${patient.code}`;
        });
        patientResults.append(item);
      }
    }
    patientSearch.addEventListener('input', () => {
      paintPatients(patientSearch.value);
    });
    paintPatients('');

    const serviceSelect = el('select', { class: 'field__control', 'aria-label': 'Service' }) as HTMLSelectElement;
    for (const service of SEED_SERVICES) {
      serviceSelect.append(
        el('option', { value: service.id }, `${service.name} (${formatPrice(service.priceCents)})`),
      );
    }
    serviceSelect.value = CONSULTATION_SERVICE_ID;

    const providerSelect = el('select', { class: 'field__control', 'aria-label': 'Provider' }) as HTMLSelectElement;
    for (const provider of providers) {
      providerSelect.append(el('option', { value: provider.id }, provider.displayName));
    }
    if (target.providerId && providers.some((provider) => provider.id === target.providerId)) {
      providerSelect.value = target.providerId;
    }

    const cardToggle = el('input', {
      class: 'field__check',
      type: 'checkbox',
      id: 'card-required',
    }) as HTMLInputElement;
    const bypass = el('input', {
      class: 'field__check',
      type: 'checkbox',
      id: 'card-bypass',
    }) as HTMLInputElement;
    const error = el('p', { class: 'cal-error', hidden: 'true', 'data-slot': 'book-error' });

    function syncCardFromService(): void {
      const service = findService(serviceSelect.value);
      cardToggle.checked = service?.cardOnFileRequired ?? false;
    }
    syncCardFromService();
    serviceSelect.addEventListener('change', () => {
      syncCardFromService();
    });

    const book = el('button', { class: 'door__button door__button--live cal-book', type: 'button' }, 'Book appointment');
    book.addEventListener('click', () => {
      const service = findService(serviceSelect.value);
      if (!selectedPatient || !service) {
        error.textContent = 'Select a seed patient and a service.';
        error.removeAttribute('hidden');
        return;
      }
      const input = {
        locationId: location.id,
        patientId: selectedPatient.id,
        serviceId: service.id,
        providerId: providerSelect.value,
        startMs: start.getTime(),
        cardRequired: cardToggle.checked,
        cardBypassed: bypass.checked,
      };
      const gate = canBookAppointment(input);
      if (!gate.ok) {
        error.textContent = gate.reason;
        error.removeAttribute('hidden');
        return;
      }
      bookAppointment(input, storage);
      closeDrawer();
      paint();
    });

    openDrawer(
      el(
        'div',
        { class: 'cal-drawer__body' },
        title,
        el('p', { class: 'note' }, `${location.name} · ${formatDayHeading(target.day)} · ${formatClock(target.minutes)}`),
        field('Patient', el('div', {}, patientSearch, patientResults, patientSelected)),
        field('Service', serviceSelect),
        field('Provider', providerSelect),
        el(
          'label',
          { class: 'field field--check', for: 'card-required' },
          cardToggle,
          el('span', {}, 'Card on file required'),
        ),
        el(
          'label',
          { class: 'field field--check', for: 'card-bypass' },
          bypass,
          el('span', {}, 'Bypass card requirement (staff override)'),
        ),
        error,
        book,
      ),
    );
  }

  function openDetails(appointmentId: string): void {
    const appointment = appointments().find((row) => row.id === appointmentId);
    if (!appointment) {
      return;
    }
    const patient = findPatient(appointment.patientId);
    const service = findService(appointment.serviceId);
    const provider = findProvider(appointment.providerId);
    const notes = el('textarea', {
      class: 'field__control field__control--notes',
      'aria-label': 'Appointment notes',
      rows: '6',
    }) as HTMLTextAreaElement;
    notes.value = appointment.notes;
    const saved = el('p', { class: 'note', 'data-slot': 'notes-status' }, 'Notes save as you type.');
    notes.addEventListener('input', () => {
      updateAppointmentNotes(appointment.id, notes.value, storage);
      saved.textContent = 'Notes saved in this preview session.';
    });

    const checkout = el('a', { class: 'door__button door__button--live cal-book', href: '#/checkout' }, 'Checkout');

    openDrawer(
      el(
        'div',
        { class: 'cal-drawer__body' },
        el('header', { class: 'cal-drawer__head' }, el('h2', {}, 'Appointment'), closeButton()),
        el(
          'dl',
          { class: 'facts' },
          el('dt', {}, 'Patient'),
          el('dd', {}, patient?.displayName ?? appointment.patientId),
          el('dt', {}, 'Service'),
          el('dd', {}, service ? `${service.name} (${formatPrice(service.priceCents)})` : appointment.serviceId),
          el('dt', {}, 'Provider'),
          el('dd', {}, provider?.displayName ?? appointment.providerId),
          el('dt', {}, 'When'),
          el('dd', {}, formatTimeRange(appointment.startMs, appointment.endMs)),
          el('dt', {}, 'Location'),
          el('dd', {}, `${location.name} · ${location.code}`),
          el('dt', {}, 'Card'),
          el(
            'dd',
            {},
            appointment.cardRequired
              ? appointment.cardBypassed
                ? 'Required — staff bypass'
                : 'Required'
              : 'Not required',
          ),
        ),
        field('Appointment notes', notes),
        saved,
        checkout,
      ),
    );
  }

  function closeButton(): HTMLButtonElement {
    const button = el('button', { class: 'cal-iconbtn', type: 'button', 'aria-label': 'Close' }, '×');
    button.addEventListener('click', () => {
      closeDrawer();
    });
    return button;
  }

  paint();
}

function field(label: string, control: HTMLElement): HTMLElement {
  return el('label', { class: 'field' }, el('span', { class: 'field__label' }, label), control);
}
